import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Key, Plus, Copy, Trash2, ExternalLink, Clock, CheckCircle2 } from "lucide-react";
import { logger } from "@/lib/logger";
import { useImpersonation } from "@/contexts/ImpersonationContext";

interface ApiKey {
  id: string;
  name: string;
  description: string | null;
  key_prefix: string;
  permissions: Record<string, string[]>;
  rate_limit_tier: string;
  last_used_at: string | null;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

interface NewKeyResponse {
  success: boolean;
  key: ApiKey & { full_key: string };
  warning: string;
}

// Full permissions granted by default for simplicity
const FULL_PERMISSIONS = {
  reports: ["read", "create", "update", "delete", "publish"],
  comps: ["read", "create", "delete"],
  showings: ["read", "create", "update", "delete"],
  social: ["read", "import"],
  insights: ["read", "generate"],
  team: ["read"],
};

export const ApiKeysManager = () => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [newKeyData, setNewKeyData] = useState<NewKeyResponse | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);

  // Form state
  const [keyName, setKeyName] = useState("");
  const [keyDescription, setKeyDescription] = useState("");
  const [expiresInDays, setExpiresInDays] = useState<string>("");

  const { toast } = useToast();
  const { getEffectiveUserId } = useImpersonation();

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Get effective user ID (impersonated or actual)
      const effectiveUserId = await getEffectiveUserId();

      // For GET requests, pass effectiveUserId in headers (body not supported for GET)
      const headers: Record<string, string> = {};
      if (effectiveUserId) {
        headers['X-Impersonated-User-Id'] = effectiveUserId;
      }

      const response = await supabase.functions.invoke("api-keys", {
        method: "GET",
        headers,
      });

      if (response.error) throw response.error;
      setKeys(response.data.keys || []);
    } catch (error) {
      logger.error("Error fetching API keys:", error);
      toast({
        title: "Error",
        description: "Failed to load API keys",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!keyName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a key name",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      // Get effective user ID (impersonated or actual)
      const effectiveUserId = await getEffectiveUserId();
      
      const response = await supabase.functions.invoke("api-keys", {
        method: "POST",
        body: {
          effectiveUserId,
          name: keyName.trim(),
          description: keyDescription.trim() || undefined,
          permissions: FULL_PERMISSIONS,
          expiresInDays: expiresInDays ? parseInt(expiresInDays) : undefined,
        },
      });

      if (response.error) throw response.error;

      setNewKeyData(response.data);
      setShowCreateDialog(false);
      setShowKeyDialog(true);
      fetchKeys();

      // Reset form
      setKeyName("");
      setKeyDescription("");
      setExpiresInDays("");
    } catch (error) {
      logger.error("Error creating API key:", error);
      toast({
        title: "Error",
        description: "Failed to create API key",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevokeKey = async (keyId: string) => {
    try {
      // Get effective user ID (impersonated or actual)
      const effectiveUserId = await getEffectiveUserId();
      
      const response = await supabase.functions.invoke("api-keys", {
        method: "DELETE",
        body: { effectiveUserId, keyId },
      });

      if (response.error) throw response.error;

      toast({
        title: "Success",
        description: "API key revoked successfully",
      });
      fetchKeys();
    } catch (error) {
      logger.error("Error revoking API key:", error);
      toast({
        title: "Error",
        description: "Failed to revoke API key",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
    toast({
      title: "Copied!",
      description: "API key copied to clipboard",
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              API Keys
            </CardTitle>
            <CardDescription>
              Create and manage API keys for external integrations
            </CardDescription>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                Create API Key
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create New API Key</DialogTitle>
                <DialogDescription>
                  Generate a new API key for integrations with Zapier, Make.com, or custom applications. Keys have full access to your team's data.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="key-name">Key Name *</Label>
                  <Input
                    id="key-name"
                    placeholder="e.g., Zapier Integration"
                    value={keyName}
                    onChange={(e) => setKeyName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="key-description">Description (optional)</Label>
                  <Textarea
                    id="key-description"
                    placeholder="What will this key be used for?"
                    value={keyDescription}
                    onChange={(e) => setKeyDescription(e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="expires">Expires In (optional)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="expires"
                      type="number"
                      placeholder="90"
                      value={expiresInDays}
                      onChange={(e) => setExpiresInDays(e.target.value)}
                      className="w-24"
                    />
                    <span className="text-sm text-muted-foreground">days (leave empty for no expiration)</span>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateKey} disabled={isCreating}>
                  {isCreating ? "Creating..." : "Create Key"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Loading API keys...</p>
          ) : keys.length === 0 ? (
            <div className="text-center py-12">
              <Key className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No API Keys</h3>
              <p className="text-muted-foreground mb-4">
                Create your first API key to start integrating with external services.
              </p>
              <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Create API Key
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {keys.map((key) => (
                <div
                  key={key.id}
                  className={`border rounded-lg p-4 ${!key.is_active ? "opacity-50" : ""}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{key.name}</h4>
                        {!key.is_active && <Badge variant="destructive">Revoked</Badge>}
                        {key.expires_at && new Date(key.expires_at) < new Date() && (
                          <Badge variant="secondary">Expired</Badge>
                        )}
                      </div>
                      {key.description && (
                        <p className="text-sm text-muted-foreground">{key.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
                        <span className="font-mono bg-muted px-2 py-1 rounded">
                          {key.key_prefix}...
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Last used: {formatRelativeTime(key.last_used_at)}
                        </span>
                        <span>Created: {formatDate(key.created_at)}</span>
                      </div>
                      <div className="mt-2">
                        <Badge variant="outline" className="text-xs">
                          Full Access
                        </Badge>
                      </div>
                    </div>
                    {key.is_active && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Revoke API Key?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will immediately disable the API key "{key.name}". Any integrations using this key will stop working. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleRevokeKey(key.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Revoke Key
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Key Display Dialog */}
      <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              API Key Created
            </DialogTitle>
            <DialogDescription>
              Copy your API key now. You won't be able to see it again!
            </DialogDescription>
          </DialogHeader>
          {newKeyData && (
            <div className="space-y-4 py-4">
              <div className="bg-muted p-4 rounded-lg">
                <Label className="text-xs font-medium mb-2 block">Your API Key</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm break-all font-mono bg-background p-2 rounded border">
                    {newKeyData.key.full_key}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(newKeyData.key.full_key)}
                    className="shrink-0"
                  >
                    {copiedKey ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900 p-3 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Important:</strong> {newKeyData.warning}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowKeyDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documentation Link */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold mb-1">API Documentation</h3>
              <p className="text-sm text-muted-foreground">
                Learn how to integrate with the North API
              </p>
            </div>
            <Button variant="outline" asChild className="gap-2">
              <a href="https://docs.northreports.com" target="_blank" rel="noopener noreferrer">
                View Docs
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
